import { useContext, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery, useApolloClient } from '@apollo/client';
import {
  Table,
  Loader,
  IColumn,
  SvgIcon,
  IWebinar,
  getDuration,
  LabelWithIcon,
  ILearningTable,
  ILearningTableData,
  Typography,
  IDashboardDetailsInfo,
  ILearningItemCollection,
} from '@ascd/witsby-components';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { debounce, get, isEmpty, startCase } from 'lodash';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { LearnosityReportTable } from '@components/LearnosityReportTable';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, IDetailsPage, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_COLLECTION_DETAILS_INFO from '@graphql/schema/dashboardCollectionDetailsInfo.graphql';
import GET_EBOOK_URL from '@graphql/schema/getEbookUrl.graphql';
import LOG_EVENTS from '@graphql/schema/mutations/logEvents.graphql';
import {
  ICourse,
  IEBook,
  IElMagazine,
  IFilterCourseList,
  ITableFilter,
  ISelectedReport,
  IReportData,
} from '@types';
import {
  handleGraphqlError,
  T_DASHBOARD_FILTER_TYPES,
  getDashboardProgressStatus,
  progressStatusColorCodes,
  getDashboardProgress,
  showToast,
  dateFormat,
  pdfGenerator,
} from '@utils';

interface IGroupForViews {
  totalViews: number;
}

const AdminDashboardCollectionTypeDetails = () => {
  const apolloClient = useApolloClient();

  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { detailsPage, sideBarApiFilter },
  } = useContext(DashboardContext);

  const { detailsPageData } = detailsPage as IDetailsPage;
  const {
    value: { _id: dashboardCollectionDetailsInfoId },
    prevScreen,
  } = detailsPageData;

  const collectionType = detailsPageData?.collectionType as T_DASHBOARD_FILTER_TYPES;
  let title = get(detailsPageData, 'value.data');
  title = get(detailsPageData, 'value.title');

  const {
    assignedBy,
    internalId1,
    selectedInstitution,
    createdByOrganizationId,
    selectedGroup,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const [selectedReportData, setSelectedReportData] = useState<ISelectedReport | null>(null);
  const [isAssessmentReportOpen, setAssessmentReportOpen] = useState(false);
  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    sortDirection: 'DESC',
    sortField: 'updatedAt',
    searchField: ['userInfo.name'],
  });
  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    filter,
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ALL' ||
      currentSelectedInstitution?.label === 'ASCD'
        ? null
        : internalId1.map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
    type: collectionType === 'RESOURCES' ? 'LEARNING_ITEMS' : collectionType,
    dashboardCollectionDetailsInfoId,
  };

  const { loading, data: dashboardCollectionDetailsInfoData } = useQuery(
    DASHBOARD_COLLECTION_DETAILS_INFO,
    {
      variables,
      fetchPolicy: 'network-only',
    },
  );

  const dashboardCollectionDetailsInfoList = get(
    dashboardCollectionDetailsInfoData,
    'dashboardCollectionDetailsInfo[0]',
    {
      users: [],
      totalCount: 0,
      totalClockHours: 0,
      totalCompletedCount: 0,
      dashBoardType: collectionType,
    },
  );

  const [logEvents] = useMutation(LOG_EVENTS);

  // when click on ebooks it's fetch url and open pdf
  const onOpenEBook = async (item?: IEBook | ICourse | IFilterCourseList) => {
    if (!Object.prototype.hasOwnProperty.call(item, 'LockLizardBookId')) return;
    if ('LockLizardBookId' in (item as ICourse | IEBook | IFilterCourseList)) {
      const response = await apolloClient.query({
        query: GET_EBOOK_URL,
        variables: { docId: (item as IEBook)?.LockLizardBookId },
      });
      if (response?.data?.getEbookUrl) {
        logEvents({
          variables: {
            data: item,
            logType: 'EBOOK',
            actionType: 'VIEWED',
          },
        });
        window.open(response.data.getEbookUrl, '_blank');
      }
    }
  };

  const todayDate = new Date().getTime();

  const learningColumn: IColumn[] = [
    {
      key: 'firstName',
      name: 'Learner Name',
      width: '35%',
      isSorting: true,
      ...(filter.sortField === 'firstName' && {
        sortType: filter.sortDirection,
      }),
      render: (_, value) => {
        const { data, firstName, lastName } = value as ILearningTable;
        return (
          <Box>
            {(collectionType === 'COURSES' ||
              collectionType === 'LEARNING_LISTS' ||
              collectionType === 'STRATEGY') && (
              <Box>
                {firstName} {lastName}
              </Box>
            )}
            {(collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES') && (
              <Box>
                {firstName} {lastName}
              </Box>
            )}
            {collectionType === 'WEBINARS' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && (data as IWebinar)?.slug) {
                    window.open(
                      `https://www.ascd.org/el/${(data as IWebinar)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}
                onClick={() => {
                  if ((data as IWebinar)?.slug) {
                    window.open(
                      `https://www.ascd.org/el/${(data as IWebinar)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}>
                {(data as IWebinar)?.title}
              </Box>
            )}
            {collectionType === 'EBOOKS' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onClick={() => onOpenEBook(data as IEBook)}>
                {(data as IEBook)?.title}
              </Box>
            )}
            {collectionType === 'EL_MAGAZINES' && (
              <Box
                tabIndex={0}
                sx={{
                  cursor: 'pointer',
                  '&:hover,&:focus': {
                    textDecoration: 'underline',
                    outline: 'none',
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
                onClick={() => {
                  if ((data as IElMagazine)?.slug) {
                    window.open(
                      `https://www.ascd.org/el/${(data as IElMagazine)?.slug}`,
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }
                }}>
                {(data as IElMagazine)?.title}
              </Box>
            )}
          </Box>
        );
      },
    },
    // {
    //   key: 'assigned',
    //   name: 'Assigned',
    //   isSorting: true,
    //   ...(filter.sortField === 'views' && {
    //     sortType: filter.sortDirection,
    //   }),
    // },
    ...(collectionType === 'COURSES' ||
    collectionType === 'LEARNING_LISTS' ||
    collectionType === 'LEARNING_ITEMS' ||
    collectionType === 'STRATEGY' ||
    collectionType === 'RESOURCES'
      ? ([
          {
            key:
              collectionType === 'COURSES' ||
              collectionType === 'LEARNING_LISTS' ||
              collectionType === 'STRATEGY'
                ? 'data.userLearningItemCollection.createdAt'
                : 'data.trackingLearningItem.createdAt',
            name: 'Date Started',
            align: 'center',
            headerAlign: 'center',
            isSorting: true,
            ...(filter.sortField ===
              (collectionType === 'COURSES' ||
              collectionType === 'LEARNING_LISTS' ||
              collectionType === 'STRATEGY'
                ? 'data.userLearningItemCollection.createdAt'
                : 'data.trackingLearningItem.createdAt') && {
              sortType: filter.sortDirection,
            }),
            render: (_, value) => {
              const { data } = value as ILearningTable;
              return (
                <Box>
                  {(collectionType === 'COURSES' ||
                    collectionType === 'LEARNING_LISTS' ||
                    collectionType === 'STRATEGY') && (
                    <Box>
                      {DateTime.fromJSDate(
                        new Date(
                          (data as ILearningTableData)?.userLearningItemCollection
                            ?.createdAt as string,
                        ),
                      ).toFormat(`''LL'/'dd'/'yyyy'`)}
                    </Box>
                  )}
                  {(collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES') && (
                    <Box>
                      {DateTime.fromJSDate(
                        new Date(
                          (data as ILearningTableData)?.trackingLearningItem?.createdAt as string,
                        ),
                      ).toFormat(`''LL'/'dd'/'yyyy'`)}
                    </Box>
                  )}
                </Box>
              );
            },
          },
          {
            key:
              collectionType === 'COURSES' ||
              collectionType === 'LEARNING_LISTS' ||
              collectionType === 'STRATEGY'
                ? 'data.userLearningItemCollection.updatedAt'
                : 'data.trackingLearningItem.createdAt',
            name: 'Date Completed',
            align: 'center',
            headerAlign: 'center',
            isSorting: true,
            render: (_, value) => {
              const { data } = value as ILearningTable;
              return (
                <Box>
                  {(collectionType === 'COURSES' ||
                    collectionType === 'LEARNING_LISTS' ||
                    collectionType === 'STRATEGY') && (
                    <Box>
                      {(data as ILearningTableData)?.userLearningItemCollection
                        ?.userCollectionStatus === 'COMPLETED'
                        ? DateTime.fromJSDate(
                            new Date(
                              (data as ILearningTableData)?.userLearningItemCollection
                                ?.updatedAt as string,
                            ),
                          ).toFormat(`''LL'/'dd'/'yyyy'`)
                        : '-'}
                    </Box>
                  )}
                  {(collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES') && (
                    <Box>
                      {(data as ILearningTableData)?.trackingLearningItem?.action === 'COMPLETED'
                        ? DateTime.fromJSDate(
                            new Date(
                              (data as ILearningTableData)?.trackingLearningItem
                                ?.updatedAt as string,
                            ),
                          ).toFormat(`''LL'/'dd'/'yyyy'`)
                        : '-'}
                    </Box>
                  )}
                </Box>
              );
            },
            ...(filter.sortField ===
              (collectionType === 'COURSES' ||
              collectionType === 'LEARNING_LISTS' ||
              collectionType === 'STRATEGY'
                ? 'data.userLearningItemCollection.updatedAt'
                : 'added') && {
              sortType: filter.sortDirection,
            }),
          },
          {
            key: 'progress',
            name: 'Progress (in hours)',
            render: (_, value) => {
              const { data } = value as ILearningTable;
              return (
                <Box>
                  <Box
                    sx={{
                      fontFamily: (theme) => [theme.font.family.PRIMARY].join(','),
                      fontSize: (theme) => theme.font.size.X_SMALL,
                      fontWeight: (theme) => theme.font.weight.BOLD,
                    }}>
                    {(collectionType === 'COURSES' ||
                      collectionType === 'LEARNING_LISTS' ||
                      collectionType === 'STRATEGY') && (
                      <Box
                        sx={{
                          background: (theme) => theme.palette.grey[500],
                          width: '8.75rem',
                          height: '1.438rem',
                        }}>
                        {((data as ILearningTableData)?.userLearningItemCollection
                          ?.userCollectionStatus === 'ADD' ||
                          (data as ILearningTableData)?.userLearningItemCollection
                            ?.userCollectionStatus === 'ENROLL') && (
                          <Box
                            sx={{
                              width: '100%',
                              color: '#FFFFFF',
                              height: '1.438rem',
                              background: '#e2252b',
                            }}>
                            <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                              0m/
                              {getDuration(
                                (data as ILearningTableData)?.userLearningItemCollection
                                  ?.totalAllocatedTime || 0,
                                false,
                              )}
                            </Box>
                          </Box>
                        )}
                        {((data as ILearningTableData)?.userLearningItemCollection
                          ?.userCollectionStatus === 'RESUME' ||
                          (data as ILearningTableData)?.userLearningItemCollection
                            ?.userCollectionStatus === 'START') && (
                          <Box
                            sx={{
                              background: 'yellow',
                              width: `${Math.round(
                                ((((data as ILearningTableData)?.userLearningItemCollection
                                  ?.totalAllocatedTime || 0) -
                                  ((data as ILearningTableData)?.userLearningItemCollection
                                    ?.totalEarnedTime || 0)) /
                                  ((data as ILearningTableData)?.userLearningItemCollection
                                    ?.totalAllocatedTime || 1)) *
                                  100,
                              )}%`,
                              height: '1.438rem',
                              color: '#000000',
                            }}>
                            <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                              {getDuration(
                                ((data as ILearningTableData)?.userLearningItemCollection
                                  ?.totalAllocatedTime || 0) -
                                  ((data as ILearningTableData)?.userLearningItemCollection
                                    ?.totalEarnedTime || 0),
                                false,
                              )}
                              /
                              {getDuration(
                                (data as ILearningTableData)?.userLearningItemCollection
                                  ?.totalAllocatedTime || 0,
                                false,
                              )}
                            </Box>
                          </Box>
                        )}
                        {(data as ILearningTableData)?.userLearningItemCollection
                          ?.userCollectionStatus === 'COMPLETED' && (
                          <Box
                            sx={{
                              background: '#03c04a',
                              width: '100%',
                              height: '1.438rem',
                              color: '#FFFFFF',
                            }}>
                            <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                              {getDuration(
                                ((data as ILearningTableData)?.userLearningItemCollection
                                  ?.totalAllocatedTime || 0) -
                                  ((data as ILearningTableData)?.userLearningItemCollection
                                    ?.totalEarnedTime || 0),
                                false,
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                    {(collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES') && (
                      <Box
                        sx={{
                          background: (theme) => theme.palette.grey[500],
                          width: '8.75rem',
                          height: '1.438rem',
                        }}>
                        {(data as ILearningTableData)?.trackingLearningItem?.action ===
                          'STARTED' && (
                          <Box
                            sx={{
                              background: 'yellow',
                              width: `${Math.round(
                                (((data as ILearningTableData)?.trackingLearningItem
                                  ?.totalConsumedTime || 0) /
                                  ((data as ILearningTableData)?.trackingLearningItem?.earnedTime ||
                                    0)) *
                                  100,
                              )}%`,
                              height: '1.438rem',
                              color: '#000000',
                            }}>
                            <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                              {getDuration(
                                (data as ILearningTableData)?.trackingLearningItem
                                  ?.totalConsumedTime || 0,
                                false,
                              )}
                              /
                              {getDuration(
                                (data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                                false,
                              )}
                            </Box>
                          </Box>
                        )}
                        {(data as ILearningTableData)?.trackingLearningItem?.action ===
                          'COMPLETED' && (
                          <Box
                            sx={{
                              background: '#03c04a',
                              width: '100%',
                              height: '1.438rem',
                              color: '#FFFFFF',
                            }}>
                            <Box sx={{ textAlign: 'center', width: '8.75rem', paddingTop: '3px' }}>
                              {getDuration(
                                (data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                                false,
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            },
          },
          ...(collectionType === 'COURSES'
            ? ([
                {
                  key: '',
                  name: '',
                  render: (_, value) => {
                    const { data, firstName, lastName } = value as ILearningTable;
                    const userName = `${firstName} ${lastName}`;
                    const courseName =
                      (data as ILearningTableData)?.learningItemCollection?.title || 'Untitled';
                    const issuedOn = (data as ILearningTableData)?.userLearningItemCollection
                      ?.certificate?.issued_on;
                    const updatedAt = (data as ILearningTableData)?.userLearningItemCollection
                      ?.updatedAt;
                    const date = DateTime.fromJSDate(new Date((issuedOn || updatedAt) as string))
                      .setZone('utc')
                      .toFormat('MM-dd-yyyy');
                    const hours = getDuration(
                      ((data as ILearningTableData)?.userLearningItemCollection
                        ?.totalAllocatedTime || 0) -
                        ((data as ILearningTableData)?.userLearningItemCollection
                          ?.totalEarnedTime || 0),
                      false,
                    );

                    const certificateUrl = (data as ILearningTableData)?.userLearningItemCollection
                      ?.certificate?.url;
                    return (
                      <Box>
                        <Box sx={{ display: 'flex', gap: '1.25rem' }}>
                          {certificateUrl && userName && courseName && hours && date && (
                            <Tooltip describeChild title="Certificate" arrow>
                              <Box>
                                <SvgIcon
                                  icon="BADGE"
                                  sx={{
                                    fill:
                                      !(data as ILearningTableData)?.userLearningItemCollection
                                        ?.certificate?.expired_on ||
                                      todayDate <=
                                        new Date(
                                          (data as ILearningTableData)?.userLearningItemCollection
                                            ?.certificate?.expired_on as string,
                                        ).getTime()
                                        ? '#00A77E'
                                        : '#EF4060',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    pdfGenerator(
                                      userName,
                                      courseName,
                                      `${hours}`,
                                      date,
                                      certificateUrl,
                                    );
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          )}
                          {get(
                            value as ILearningTable,
                            'data.learningItemCollection.settings.completionFactor.assessments',
                            [],
                          ).length > 0 && (
                            <Tooltip describeChild title="Assessment" arrow>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: (theme) => theme.font.size.SMALL,
                                }}>
                                <SvgIcon
                                  icon="ASSESSMENT"
                                  sx={{ cursor: 'pointer', fill: 'none' }}
                                  onClick={() => {
                                    setSelectedReportData({
                                      searchData: (value as ILearningTable)?.firstName,
                                      reportData: {
                                        type: (
                                          (value as ILearningTable)?.data as ILearningTableData
                                        )?.learningItemCollection?.type,
                                        id: ((value as ILearningTable)?.data as ILearningTableData)
                                          ?.learningItemCollection?._id,
                                        title: (
                                          (value as ILearningTable)?.data as ILearningTableData
                                        )?.learningItemCollection?.title,
                                      },
                                    });
                                    setAssessmentReportOpen(true);
                                  }}
                                />
                                {get(
                                  value as ILearningTable,
                                  'data.userLearningItemCollection.assessments.length',
                                  0,
                                )
                                  ? `(${get(
                                      value as ILearningTable,
                                      'data.userLearningItemCollection.completedAssessments.length',
                                      0,
                                    )}/${
                                      (data as ILearningTableData)?.userLearningItemCollection
                                        ?.assessments?.length
                                    })`
                                  : ''}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  },
                },
              ] as IColumn[])
            : []),
        ] as IColumn[])
      : []),
    ...(collectionType === 'EBOOKS' || collectionType === 'WEBINARS'
      ? ([
          {
            key: 'uniqueAccess',
            name: 'Unique Access',
            align: 'center',
            headerAlign: 'center',
            isSorting: true,
            ...(filter.sortField === 'uniqueAccess' && {
              sortType: filter.sortDirection,
            }),
          },
        ] as IColumn[])
      : []),
  ];

  const getTitle = (type: T_DASHBOARD_FILTER_TYPES): string => {
    if (loading) {
      return `${startCase(collectionType.toLowerCase())}: ${title}`;
    }
    if (
      type === 'COURSES' ||
      type === 'LEARNING_LISTS' ||
      type === 'LEARNING_ITEMS' ||
      type === 'STRATEGY'
    ) {
      return `${startCase(collectionType.toLowerCase()) || 'Learnings'}: ${title} (${
        dashboardCollectionDetailsInfoList?.totalCompletedCount
      } Completed)`;
    }

    if (type === 'RESOURCES') {
      return `${startCase(collectionType.toLowerCase()) || 'Learnings'}: ${title} (${
        dashboardCollectionDetailsInfoList?.totalCompletedCount
      } Completed)`;
    }
    if (type === 'EBOOKS') {
      const TotalViews = dashboardCollectionDetailsInfoList?.collections[0]?.groupForViews.reduce(
        (sum: number, item: IGroupForViews) => sum + item.totalViews,
        0,
      );
      return `Ebooks: ${title} (${TotalViews} Views)`;
    }
    if (type === 'WEBINAR' || type === 'WEBINARS') {
      const TotalViews = dashboardCollectionDetailsInfoList?.collections[0]?.groupForViews.reduce(
        (sum: number, item: IGroupForViews) => sum + item.totalViews,
        0,
      );
      return `Webinars: ${title} (${TotalViews} Views)`;
    }
    if (type === 'EL_MAGAZINE' || type === 'EL_MAGAZINES') {
      const TotalViews = dashboardCollectionDetailsInfoList?.collections[0]?.groupForViews.reduce(
        (sum: number, item: IGroupForViews) => sum + item.totalViews,
        0,
      );
      return `EL Magazine: ${title} (${TotalViews} Views)`;
    }
    return `Learnings: ${title}`;
  };

  const [dashboardCollectionDetailsInfoCSV, { loading: isCSVLoading }] = useLazyQuery(
    DASHBOARD_COLLECTION_DETAILS_INFO,
    { fetchPolicy: 'network-only' },
  );

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      dashboardCollectionDetailsInfoCSV({
        variables: {
          ...variables,
          filter: {
            ...filter,
            page: 0,
            limit: dashboardCollectionDetailsInfoList.totalCount,
          },
        },
      })
        .then((res) => {
          const dashboardCollectionDetailsCSVInfo = get(
            res.data,
            'dashboardCollectionDetailsInfo[0]',
            {
              users: [],
              totalCount: 0,
              totalClockHours: 0,
              totalCompletedCount: 0,
              dashBoardType: collectionType,
            },
          );

          let dataList = [];
          if (
            collectionType === 'COURSES' ||
            collectionType === 'LEARNING_LISTS' ||
            collectionType === 'LEARNING_ITEMS' ||
            collectionType === 'RESOURCES' ||
            collectionType === 'STRATEGY'
          ) {
            // !TODO: move email to interface of component lib once stage gets merge in main branch
            dataList = dashboardCollectionDetailsCSVInfo.users.map(
              (item: ILearningTable & { email: string }) => ({
                'Learner Name': `${item.firstName} ${item.lastName}`,
                Email: item.email,
                'Date Started':
                  collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES'
                    ? DateTime.fromJSDate(
                        new Date(
                          (item.data as ILearningTableData)?.trackingLearningItem
                            ?.createdAt as string,
                        ),
                      ).toFormat(`''LL'/'dd'/'yyyy'`)
                    : DateTime.fromJSDate(
                        new Date(
                          (item.data as ILearningTableData)?.userLearningItemCollection
                            ?.createdAt as string,
                        ),
                      ).toFormat(`''LL'/'dd'/'yyyy'`),
                'Date Completed':
                  collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES'
                    ? dateFormat({
                        date: (item.data as ILearningTableData)?.trackingLearningItem
                          ?.updatedAt as string,
                        format:
                          (item.data as ILearningTableData)?.trackingLearningItem?.action ===
                          'COMPLETED'
                            ? 'dd-MMM-yyyy'
                            : '-',
                      })
                    : dateFormat({
                        date: (item.data as ILearningTableData)?.userLearningItemCollection
                          ?.updatedAt as string,
                        format:
                          (item.data as ILearningTableData)?.userLearningItemCollection
                            ?.userCollectionStatus === 'COMPLETED'
                            ? 'dd-MMM-yyyy'
                            : '-',
                      }),
                'Progress (in hours)': getDashboardProgress(
                  collectionType,
                  (item.data as ILearningTableData)?.userLearningItemCollection
                    ?.userCollectionStatus ||
                    (item.data as ILearningTableData)?.trackingLearningItem?.action,
                  collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES'
                    ? getDuration(
                        (item.data as ILearningTableData)?.trackingLearningItem
                          ?.totalConsumedTime || 0,
                        false,
                      )
                    : getDuration(
                        ((item.data as ILearningTableData)?.userLearningItemCollection
                          ?.totalAllocatedTime || 0) -
                          ((item.data as ILearningTableData)?.userLearningItemCollection
                            ?.totalEarnedTime || 0),
                        false,
                      ),
                  collectionType === 'LEARNING_ITEMS' || collectionType === 'RESOURCES'
                    ? getDuration(
                        (item.data as ILearningTableData)?.trackingLearningItem?.earnedTime || 0,
                        false,
                      )
                    : getDuration(
                        (item.data as ILearningTableData)?.userLearningItemCollection
                          ?.totalAllocatedTime || 0,
                        false,
                      ),
                ),
                Status: getDashboardProgressStatus(
                  collectionType,
                  (item.data as ILearningTableData)?.trackingLearningItem?.action ||
                    (item.data as ILearningTableData)?.userLearningItemCollection
                      ?.userCollectionStatus,
                ),
              }),
            );
          }
          // if (collectionType === 'COURSES' || collectionType === 'LEARNING_LISTS') {
          //   dataList = dashboardCollectionDetailsCSVInfo.users.map((item: ILearningTable) => ({
          //     Name:
          //       (item?.data as ILearningTableData)?.learningItemCollection?.title ||
          //       (item?.data as ILearningTableData)?.learningItem?.title ||
          //       (item?.data as ILearningTableData)?.learningItemCollection?.title ||
          //       (item?.data as IEBook)?.title ||
          //       (item?.data as IWebinar)?.title ||
          //       (item?.data as IElMagazine)?.title ||
          //       'Untitled',
          //     Type: DASHBOARD_COUNTS_TYPES[item.type],
          //     Status: getDashboardProgressStatus(
          //       item.type,
          //       (item.data as ILearningTableData)?.trackingLearningItem?.action ||
          //         (item.data as ILearningTableData)?.userLearningItemCollection
          //           ?.userCollectionStatus,
          //     ),
          //     'Completed Date': DateTime.fromJSDate(new Date(item?.updatedAt as string)).toFormat(
          //       `''LL'/'dd'/'yyyy'`,
          //     ),
          //   }));
          // }

          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(dataList);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const data = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            data,
            `${startCase(collectionType.toLowerCase())} ${title} (${dateFormat({
              date: startDate,
              format: 'dd-MMM-yyyy',
            })} - ${dateFormat({ date: endDate, format: 'dd-MMM-yyyy' })}) ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };
  if (isAssessmentReportOpen) {
    return (
      <LearnosityReportTable
        searchData={selectedReportData?.searchData}
        reportData={selectedReportData?.reportData as IReportData}
        setAssessmentReportOpen={setAssessmentReportOpen}
      />
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            <Table
              isSearchEnable
              searchPlaceHolder="Search Learner"
              loading={loading}
              page={filter.page}
              apiPaginationEnable
              pageSize={filter.limit}
              columns={learningColumn}
              searchValue={filter.searchText}
              activeSortField={filter.sortField}
              data={dashboardCollectionDetailsInfoList.users}
              count={dashboardCollectionDetailsInfoList.totalCount}
              dataEmptyChildren={<Box>No learner found</Box>}
              handlePageChange={(_, newPage) => {
                setFilter({ ...filter, page: newPage });
              }}
              onHandleSearch={debounce((e) => {
                setFilter({ ...filter, page: 0, searchText: e.target.value });
              }, 500)}
              handleRowsPerPageChange={(e) => {
                setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
              }}
              onSortClick={(key, sortType) => {
                setFilter({
                  ...filter,
                  page: 0,
                  sortField: key as string,
                  sortDirection: sortType,
                });
              }}
              headerChildren={
                <Box>
                  <LabelWithIcon
                    title={getTitle(collectionType)}
                    iconName="MOVE_UP"
                    iconDirection="left"
                    iconColorHex="#FFFFFF"
                    sx={{ color: '#FFFFFF' }}
                    onClick={() => {
                      dispatch({
                        type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
                        data: {
                          type: prevScreen,
                          detailsPageData: {
                            selectedCollections: [collectionType],
                            collectionType,
                          },
                        },
                      });
                    }}
                    IconTitleDistance={1}
                    iconSx={{ transform: 'rotate(270deg)' }}
                  />
                </Box>
              }
              headerExtra={
                dashboardCollectionDetailsInfoList.totalCount > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {isCSVLoading ? (
                      <Loader
                        sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }}
                        size={20}
                      />
                    ) : (
                      <Tooltip describeChild title="Download the report below" arrow>
                        <Box
                          sx={{
                            ml: (theme) => theme.spaces.REGULAR,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                          <IconButton onClick={() => exportToCSV()} aria-label="login activity">
                            <SvgIcon
                              icon="FILE_DOWNLOAD"
                              viewBox="0 0 22 29"
                              sx={{ fill: '#FFFF' }}
                            />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )
              }
              getRowSx={(row) => {
                if (row?.courseStatus === 'UNPUBLISHED' || row?.courseStatus === 'ARCHIVED') {
                  return {
                    background: (theme) => theme.palette.grey.A100,
                  };
                }
                return undefined;
              }}
              aboveTableHeaderChildren={
                (collectionType === 'COURSES' ||
                  collectionType === 'LEARNING_LISTS' ||
                  collectionType === 'STRATEGY') && (
                  <Box
                    sx={{
                      p: 1.2,
                      pt: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                    <Box
                      sx={{
                        background: '#e7f9f4',
                        borderRadius: '50px',
                        fontSize: '11px',
                        display: 'flex',
                        gap: '30px',
                        pl: '5px',
                        height: '31px',
                        width: '340px',
                        justifyContent: 'center',
                      }}>
                      {progressStatusColorCodes.map((progressStatusColorCode) => (
                        <Box
                          key={progressStatusColorCode.colorCode}
                          sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Box
                            sx={{
                              width: '16px',
                              height: '15px',
                              background: progressStatusColorCode.colorCode,
                              border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: '11px' }}
                            text={progressStatusColorCode.text}
                          />
                        </Box>
                      ))}
                    </Box>

                    {collectionType === 'COURSES' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1.563rem' }}>
                        <LabelWithIcon
                          title="Certificate"
                          iconName="BADGE"
                          iconDirection="left"
                          iconColorHex="#00A77E"
                          isClickable={false}
                          sx={{ fontWeight: (theme) => theme.font.weight.LIGHT }}
                        />
                        <LabelWithIcon
                          title="Assessment"
                          iconName="ASSESSMENT"
                          iconDirection="left"
                          iconColorHex="none"
                          isClickable={false}
                          sx={{ fontWeight: (theme) => theme.font.weight.LIGHT }}
                        />
                      </Box>
                    ) : (
                      <Box />
                    )}
                  </Box>
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboardCollectionTypeDetails;
